import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { Icon } from '@/components';
import { formatPhoneNumber } from '@/helpers';
import { SORT_ORDER, SUPER_ADMIN_SORT_BY, type SuperAdminCreateRequest, type SuperAdminUser } from '@/types';
import { SuperAdminModal } from './SuperAdminModal';
import superAdminsService from '@/services/super-admins.service';
import styles from './SuperAdmins.module.sass';
import {
  useCreateSuperAdminMutation,
  useRemoveSuperAdminMutation,
  useSuperAdmins,
  useUpdateSuperAdminMutation,
} from '@/hooks/super-admins';

const LAYOUT = {
  TABLE_FILTER_ICON_SIZE: 20,
  LIMIT: 10,
  SKELETON_HEIGHT: 30,
};

export const SuperAdmins = () => {
  const [extraItems, setExtraItems] = useState<SuperAdminUser[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<SuperAdminUser | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SORT_ORDER | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<SuperAdminUser | null>(null);

  const { data, isLoading } = useSuperAdmins({ limit: LAYOUT.LIMIT, sortBy, sortOrder });
  const createMutation = useCreateSuperAdminMutation();
  const updateMutation = useUpdateSuperAdminMutation();
  const removeMutation = useRemoveSuperAdminMutation();

  const firstPageItems = data?.items ?? [];
  const total = data?.total ?? 0;
  const items = useMemo(() => [...firstPageItems, ...extraItems], [firstPageItems, extraItems]);
  const hasMore = items.length < total;

  useEffect(() => {
    setExtraItems([]);
    if (firstPageItems.length > 0) {
      setCursor(firstPageItems[firstPageItems.length - 1].id);
    } else {
      setCursor(undefined);
    }
  }, [data]);

  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await superAdminsService.list({
        limit: LAYOUT.LIMIT,
        cursor,
        sortBy,
        sortOrder,
      });
      setExtraItems((prev) => [...prev, ...response.items]);
      if (response.items?.length > 0) {
        setCursor(response.items[response.items.length - 1].id);
      }
    } catch (e) {
      console.error('Failed to load more super admins', e);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleSave = async (data: Partial<SuperAdminCreateRequest>) => {
    if (selectedUser) {
      await updateMutation.mutateAsync({ id: selectedUser.id, data });
    } else {
      await createMutation.mutateAsync(data as SuperAdminCreateRequest);
    }
    setIsModalOpen(false);
    setSelectedUser(undefined);
  };

  const handleEdit = async (id: string) => {
    try {
      const user = await superAdminsService.getById(id);
      setSelectedUser(user);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch user details', error);
    }
  };

  const handleDelete = (user: SuperAdminUser) => {
    setConfirmDelete(user);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    await removeMutation.mutateAsync(confirmDelete.id);
    setConfirmDelete(null);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
    } else {
      setSortBy(column);
      setSortOrder(SORT_ORDER.ASC);
    }
  };

  const skeletonRows = Array.from({ length: LAYOUT.LIMIT }).map((_, index) => (
    <Table.Tr key={`skeleton-${index}`}>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="70%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="60%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="50%" radius="sm" />
      </Table.Td>
      <Table.Td className={styles.actionsColumn}>
        <Group gap="xs">
          <Skeleton height={LAYOUT.TABLE_FILTER_ICON_SIZE} width={LAYOUT.TABLE_FILTER_ICON_SIZE} radius="sm" />
          <Skeleton height={LAYOUT.TABLE_FILTER_ICON_SIZE} width={LAYOUT.TABLE_FILTER_ICON_SIZE} radius="sm" />
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  const rows = items?.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{`${item.firstName} ${item.lastName}`}</Table.Td>
      <Table.Td>{item.email}</Table.Td>
      <Table.Td>{formatPhoneNumber(item.phoneNumber || item.mobilePhone || '')}</Table.Td>
      <Table.Td className={styles.actionsColumn}>
        <Group gap="xs">
          <ActionIcon aria-label="edit" variant="subtle" onClick={() => handleEdit(item.id)}>
            <Icon name="IconEdit" size={LAYOUT.TABLE_FILTER_ICON_SIZE} />
          </ActionIcon>
          <ActionIcon aria-label="delete" variant="subtle" color="red" onClick={() => handleDelete(item)}>
            <Icon name="IconTrash" size={LAYOUT.TABLE_FILTER_ICON_SIZE} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack px="xxl" py="md" gap="md">
      <Group justify="flex-start">
        <Button leftSection={<Icon name="IconPlus" />} onClick={() => setIsModalOpen(true)}>
          New Super Admin
        </Button>
      </Group>

      <Paper radius="md" pos="relative" p="xl">
        <Title order={3} mb="md">
          Super Admins
        </Title>
        <Table striped highlightOnHover withRowBorders withColumnBorders withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Group gap={4}>
                  <Text fw={700} fz="sm">
                    Name
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color={sortBy === SUPER_ADMIN_SORT_BY.NAME ? 'primary' : 'gray'}
                    onClick={() => handleSort(SUPER_ADMIN_SORT_BY.NAME)}
                  >
                    <Icon
                      name={
                        sortBy === SUPER_ADMIN_SORT_BY.NAME
                          ? sortOrder === SORT_ORDER.ASC
                            ? 'IconArrowUp'
                            : 'IconArrowDown'
                          : 'IconSelector'
                      }
                      size={16}
                    />
                  </ActionIcon>
                </Group>
              </Table.Th>
              <Table.Th>
                <Group gap={4}>
                  <Text fw={700} fz="sm">
                    Email
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color={sortBy === SUPER_ADMIN_SORT_BY.EMAIL ? 'primary' : 'gray'}
                    onClick={() => handleSort(SUPER_ADMIN_SORT_BY.EMAIL)}
                  >
                    <Icon
                      name={
                        sortBy === SUPER_ADMIN_SORT_BY.EMAIL
                          ? sortOrder === SORT_ORDER.ASC
                            ? 'IconArrowUp'
                            : 'IconArrowDown'
                          : 'IconSelector'
                      }
                      size={16}
                    />
                  </ActionIcon>
                </Group>
              </Table.Th>
              <Table.Th>Phone Number</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? skeletonRows : rows}
            {!isLoading && items?.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">No super admins found</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <SimpleGrid cols={3} spacing="md">
        <Box></Box>
        <Group justify="center">
          <Button
            variant="default"
            disabled={isLoading || isLoadingMore || !hasMore}
            loading={isLoadingMore}
            onClick={handleLoadMore}
          >
            {isLoadingMore ? 'Loading...' : hasMore ? 'Load More' : 'No More Results'}
          </Button>
        </Group>
        <Group justify="flex-end">
          <Paper className={styles.paginationCounter} py="xs" px="md" radius="md">
            {isLoading ? (
              <Skeleton height={LAYOUT.SKELETON_HEIGHT} width={60} radius="sm" />
            ) : (
              <Text>{`${items?.length} / ${total}`}</Text>
            )}
          </Paper>
        </Group>
      </SimpleGrid>

      <SuperAdminModal
        opened={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(undefined);
        }}
        onSave={handleSave}
        user={selectedUser}
      />

      <Modal
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Delete Super Admin?"
        centered
      >
        <Stack mt="md">
          <Text>Are you sure you want to delete this super admin?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(null)}>
              No
            </Button>
            <Button color="red" onClick={handleConfirmDelete}>
              Yes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
